/**
 * Trilha de auditoria da empresa e historico de alteracoes de uma entidade.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';

import { requestPage } from '../client';

/** Uma linha da trilha: quem fez o que, quando e de onde. */
export interface AuditRecord {
  id: string;
  actor_type: string;
  actor_label: string;
  /** Nome de quem fez, pelo cadastro da pessoa (P-LOG). */
  actor_name?: string;
  /**
   * Rotulo e nome sairam mascarados: quem le nao tem usuarios.usuario.view, como em
   * Acessos (`person_masked`).
   */
  actor_masked?: boolean;
  action: string;
  module: string;
  entity_type: string;
  entity_id: string | null;
  ip: string;
  /** Navegador de quem fez (P-LOG). */
  user_agent?: string;
  request_id: string;
  occurred_at: string;
}

/**
 * Por que o antes e o depois nao sairam: `permissao` (sem auditoria.trilha.view) ou
 * `entidade` (sem a leitura do cadastro alterado).
 */
export type AuditStatesHiddenReason = '' | 'permissao' | 'entidade';

/** Linha da trilha pedida com `include_states=true` (P-LOG). */
export interface AuditRecordWithStates extends AuditRecord {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changed: string[];
  states_hidden: boolean;
  states_hidden_reason: AuditStatesHiddenReason;
}

/** Recortes da trilha da empresa (aba Auditoria da tela de logs). */
export interface AuditTrailParams extends ListParams {
  /** Data (AAAA-MM-DD) ou instante; o dia inteiro no horario de Brasilia. */
  from?: string;
  to?: string;
  action?: string;
  module?: string;
  /** Nome ou e-mail de quem fez, ou parte dele. */
  actor?: string;
  entity_type?: string;
  entity_id?: string;
  sort?: 'occurred_at' | '-occurred_at';
}

export const auditTrail = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<AuditRecord>(`/api/v1/audit-logs${query(params)}`, signal),
  /** A trilha com o antes e o depois, conforme a permissao de quem le. */
  listWithStates: (params: AuditTrailParams, signal?: AbortSignal) =>
    requestPage<AuditRecordWithStates>(
      `/api/v1/audit-logs${query({ ...params, include_states: true })}`,
      signal,
    ),
};

/** Um evento de acesso da empresa (aba Acessos da tela de logs, P-LOG). */
export interface SecurityEvent {
  id: string;
  /** Como `auth.login.succeeded`, `auth.logout` ou `auth.password.changed`. */
  action: string;
  user_id: string | null;
  user_name: string;
  email: string;
  /** Nome e e-mail mascarados para quem nao le os usuarios da empresa. */
  person_masked: boolean;
  ip: string;
  user_agent: string;
  /** So `reason`, `delivery`, `sessions_revoked` e `failed_attempts`. */
  details: Record<string, unknown>;
  request_id: string;
  occurred_at: string;
}

export interface SecurityEventParams extends ListParams {
  from?: string;
  to?: string;
  /** Comeco do evento: `auth.login` traz sucesso e falha. */
  action?: string;
  /** Exige a leitura dos usuarios da empresa. */
  email?: string;
  sort?: 'occurred_at' | '-occurred_at';
}

export const securityEvents = {
  list: (params: SecurityEventParams, signal?: AbortSignal) =>
    requestPage<SecurityEvent>(`/api/v1/security-events${query(params)}`, signal),
};

/** Situacao de uma execucao de rotina automatica. */
export type JobRunStatus = 'running' | 'succeeded' | 'failed' | 'skipped';

/** Uma execucao de rotina automatica (aba Tarefas e GetJobLogs da cotacao). */
export interface JobRun {
  id: string;
  /** Nome da rotina, no formato `modulo.rotina`. */
  job: string;
  subject_type: string;
  subject_id: string | null;
  status: JobRunStatus;
  message: string;
  started_at: string;
  finished_at: string | null;
}

export interface JobRunParams extends ListParams {
  from?: string;
  to?: string;
  job?: string;
  status?: JobRunStatus;
  subject_type?: string;
  subject_id?: string;
  sort?: 'occurred_at' | '-occurred_at';
}

export const jobRuns = {
  list: (params: JobRunParams, signal?: AbortSignal) =>
    requestPage<JobRun>(`/api/v1/job-runs${query(params)}`, signal),
  /** As execucoes sobre uma operacao, o GetJobLogs do OnPoint. */
  ofOperation: (operationId: string, params: JobRunParams = {}, signal?: AbortSignal) =>
    requestPage<JobRun>(
      `/api/v1/operations/${encodeURIComponent(operationId)}/job-logs${query(params)}`,
      signal,
    ),
};

/**
 * Tipos de entidade com historico de alteracoes (P-FUN-2b). E o mapa fechado do
 * servidor (`backend/internal/bootstrap/entity_history.go`): tipo fora dele e
 * recusado, e por isso a lista vive aqui como tipo.
 */
export type HistoryEntityType =
  | 'policyholder'
  | 'broker'
  | 'operation'
  | 'product'
  | 'modality'
  | 'modality_group'
  | 'policy'
  // Segurado (P-SEG-1).
  | 'insured'
  // Versao de parametros do motor de credito (C-ADM).
  | 'credit_parameter_set';

/** Uma alteracao da entidade, com o antes e o depois gravados na trilha. */
export interface EntityHistoryEntry {
  id: string;
  action: string;
  module: string;
  actor_type: string;
  actor_label: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /** Campos cujo valor mudou, calculados no servidor. */
  changed: string[];
  ip: string;
  user_agent: string;
  /** IP e agente omitidos para quem tem escopo de corretora. */
  origin_hidden: boolean;
  request_id: string;
  occurred_at: string;
}

export interface EntityHistoryParams {
  limit?: number;
  offset?: number;
  /** Comeco da acao, como `tomadores.limite`. */
  action?: string;
  sort?: 'occurred_at' | '-occurred_at';
}

export const entityHistory = {
  list: (
    entityType: HistoryEntityType,
    entityId: string,
    params: EntityHistoryParams = {},
    signal?: AbortSignal,
  ) =>
    requestPage<EntityHistoryEntry>(
      `/api/v1/audit-logs/entities/${entityType}/${encodeURIComponent(entityId)}${query({ ...params })}`,
      signal,
    ),
};
